export const CREATOR_PERSONA_IDS = [
  'creator',
  'business',
  'expert_coach',
  'community_builder',
  'publisher',
  'artist',
  // Deferred / legacy — kept for stored data validation
  'personal_brand',
  'mentor',
  'educator',
  'speaker',
  'agency',
  'nonprofit',
] as const;

export type CreatorPersonaId = (typeof CREATOR_PERSONA_IDS)[number];

/** Personas shown in the Studio onboarding wizard (MVP). */
export const STUDIO_ONBOARDING_VISIBLE_PERSONA_IDS = [
  'creator',
  'business',
  'expert_coach',
  'community_builder',
  'publisher',
  'artist',
] as const;

export type StudioOnboardingVisiblePersonaId =
  (typeof STUDIO_ONBOARDING_VISIBLE_PERSONA_IDS)[number];

export const STUDIO_ONBOARDING_STEP_IDS = [
  'persona',
  'monetization',
  'socials',
  'public-defaults',
  'complete',
] as const;

export type StudioOnboardingStepId = (typeof STUDIO_ONBOARDING_STEP_IDS)[number];

export type CreatorActivationSource = 'member_upgrade' | 'direct_signup';

export const MONETIZATION_INTEREST_IDS = [
  'calls_sessions',
  'ask_me_anything',
  'membership',
  'newsletter',
  'social_scheduling',
  'ai_content',
  'digital_products',
  'courses',
  'landing_pages',
  'forms_leads',
  'affiliate_links',
  'testimonials',
] as const;

export type MonetizationInterestId = (typeof MONETIZATION_INTEREST_IDS)[number];

const LEGACY_EXPERT_PERSONA_IDS = new Set(['mentor', 'educator', 'speaker']);

export function isCreatorPersonaId(value: string): value is CreatorPersonaId {
  return (CREATOR_PERSONA_IDS as readonly string[]).includes(value);
}

export function isStudioOnboardingVisiblePersona(
  value: string,
): value is StudioOnboardingVisiblePersonaId {
  return (STUDIO_ONBOARDING_VISIBLE_PERSONA_IDS as readonly string[]).includes(value);
}

export function isStudioOnboardingStepId(value: string): value is StudioOnboardingStepId {
  return (STUDIO_ONBOARDING_STEP_IDS as readonly string[]).includes(value);
}

export function isMonetizationInterestId(value: string): value is MonetizationInterestId {
  return (MONETIZATION_INTEREST_IDS as readonly string[]).includes(value);
}

/** Map legacy mentor / educator / speaker selections to the consolidated persona. */
export function normalizeStudioOnboardingPersona(value: string): CreatorPersonaId | null {
  if (LEGACY_EXPERT_PERSONA_IDS.has(value)) return 'expert_coach';
  return isCreatorPersonaId(value) ? value : null;
}

export function normalizeSecondaryPersonas(
  primary: CreatorPersonaId,
  secondary?: string[] | null,
): CreatorPersonaId[] {
  if (!secondary?.length) return [];
  const unique = secondary
    .map((id) => normalizeStudioOnboardingPersona(id) ?? (isCreatorPersonaId(id) ? id : null))
    .filter((id): id is CreatorPersonaId => !!id && id !== primary)
    .filter((id, index, arr) => arr.indexOf(id) === index)
    .filter((id) => isStudioOnboardingVisiblePersona(id));
  return unique.slice(0, 2);
}

export function resolveStudioOnboardingStepIndex(step: string | null | undefined): number {
  if (!step) return 0;
  const index = STUDIO_ONBOARDING_STEP_IDS.indexOf(step as StudioOnboardingStepId);
  return index >= 0 ? index : 0;
}

/** Platforms shown in the Studio onboarding social grid (website / custom URL excluded). */
export const STUDIO_ONBOARDING_PROFILE_PLATFORM_IDS = [
  'instagram',
  'facebook',
  'tiktok',
  'x',
  'youtube',
  'snapchat',
  'linkedin',
  'pinterest',
  'substack',
  'reddit',
  'whatsapp',
  'discord',
  'twitch',
  'vimeo',
] as const;

export type StudioOnboardingProfilePlatformId =
  (typeof STUDIO_ONBOARDING_PROFILE_PLATFORM_IDS)[number];

export const MIN_STUDIO_ONBOARDING_PLATFORM_SOCIALS = 3;

export function countStudioOnboardingPlatformSocials(
  links: ReadonlyArray<{ platform?: string | null; url?: string | null }> | null | undefined,
): number {
  if (!links?.length) return 0;
  const allowed = new Set(STUDIO_ONBOARDING_PROFILE_PLATFORM_IDS);
  const connected = new Set<string>();
  for (const link of links) {
    const platform = link.platform?.trim();
    const url = link.url?.trim();
    if (platform && url && allowed.has(platform as StudioOnboardingProfilePlatformId)) {
      connected.add(platform);
    }
  }
  return connected.size;
}

export function hasMinimumStudioOnboardingPlatformSocials(
  links: ReadonlyArray<{ platform?: string | null; url?: string | null }> | null | undefined,
): boolean {
  return countStudioOnboardingPlatformSocials(links) >= MIN_STUDIO_ONBOARDING_PLATFORM_SOCIALS;
}

/** Persona → 2–3 recommended monetization interests for onboarding UI. */
export const PERSONA_MONETIZATION_RECOMMENDATIONS: Record<
  CreatorPersonaId,
  MonetizationInterestId[]
> = {
  creator: ['digital_products', 'membership', 'newsletter'],
  business: ['forms_leads', 'landing_pages', 'testimonials'],
  expert_coach: ['calls_sessions', 'courses', 'ask_me_anything'],
  community_builder: ['membership', 'ask_me_anything', 'newsletter'],
  publisher: ['newsletter', 'digital_products', 'landing_pages'],
  artist: ['digital_products', 'landing_pages', 'membership'],
  personal_brand: ['newsletter', 'landing_pages', 'calls_sessions'],
  mentor: ['calls_sessions', 'courses', 'ask_me_anything'],
  educator: ['courses', 'membership', 'newsletter'],
  speaker: ['calls_sessions', 'forms_leads', 'landing_pages'],
  agency: ['forms_leads', 'testimonials', 'landing_pages'],
  nonprofit: ['membership', 'forms_leads', 'newsletter'],
};
